'use client';

import { Avatar } from '@/components/atoms/Avatar';
import { Link } from '@/components/atoms/Link';
import { Skeleton } from '@/components/atoms/Skeleton';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import { ShareBlog } from '@/components/molecules/ShareBlog';
import {
  ArticleCard,
  ArticleCardSkeleton,
} from '@/components/organisms/ArticleCard';
import { SubscribeBlog } from '@/components/organisms/SubscribeBlog';
import { RelatedArticlesAndCategories } from '@/components/template/BlogDetailTemplate/RelatedArticlesAndCategories';
import { getTailwindClass } from '@/components/template/BlogDetailTemplate/helpers';
import {
  ElementData,
  TweetElementData,
} from '@/components/template/BlogDetailTemplate/types';
import { useInfiniteArticles } from '@/hooks/queries/articles';
import { useArticles } from '@/hooks/queries/articles/useArticles';
import useWindowSize from '@/hooks/useWindowSize';
import { formatUtcDateToLocalTimezone } from '@/lib/dates';
import { CategoryPreview } from '@/services/wordpress/categories';
import { Maybe } from '@/types';
import { BlogPreview, MediaDetails, RawBlogType } from '@/types/blog';
import { NewspaperIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, {
  FC,
  RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Clock } from 'react-feather';
import { useInView } from 'react-intersection-observer';
import { CarAIBanner } from '@/components/organisms/CarAIBanner';
import { ArticleComments } from './ArticleComments';
import { ArticleMetaTags } from './ArticleMetaTags';
import { RelatedNews } from './RelatedNews';

export interface BlogDetailTemplateProps {
  blog: RawBlogType;
  posts: BlogPreview[];
  featuredImage: Maybe<MediaDetails>;
  articleCategories: CategoryPreview[];
  remainingCategories: CategoryPreview[];
  /** Tags parsed from the article's "Tags: ..." paragraph. */
  articleTags?: string[];
  tags: ElementData[];
  author: {
    avatarUrl: string | null;
    name: string;
    slug: string;
  };
  maxPages: number;
}

const truncateBreadcrumbTitle = (title: string): string =>
  title.length < 30 ? title : `${title.slice(0, 30)}...`;

// Create lazy loaded tweet components outside the component
const LazyTweet = dynamic(
  () => import('react-tweet').then((mod) => mod.Tweet),
  {
    loading: () => <Skeleton className="h-[300px] w-[550px]" />,
    ssr: false,
  }
);

const LazyTweetNotFound = dynamic(
  () => import('react-tweet').then((mod) => mod.TweetNotFound),
  {
    loading: () => <Skeleton className="h-[300px] w-[550px]" />,
    ssr: false,
  }
);

const LazyCarouselGallery = dynamic(
  () => import('./CarouselGallery').then((mod) => mod.CarouselGallery),
  {
    loading: () => <Skeleton className="h-[254px] w-full ml:h-[576px]" />,
    ssr: false,
  }
);

interface TweetComponentProps {
  tweetData: TweetElementData['tweetData'];
  isFirstTweet: boolean;
  isFirstTweetVisible: boolean;
  firstTweetRef: RefCallback<Element>;
}

const TweetComponent: FC<TweetComponentProps> = ({
  tweetData,
  isFirstTweet,
  isFirstTweetVisible,
  firstTweetRef,
}) => {
  const renderTweetContent = () => {
    // Show skeleton until first tweet is visible
    if (!isFirstTweetVisible) {
      return <Skeleton className="h-[300px] w-[550px]" />;
    }

    // Once first tweet is visible, load actual content
    if (tweetData) {
      return <LazyTweet id={tweetData.id_str} />;
    }

    return <LazyTweetNotFound />;
  };

  return (
    <div
      className="flex flex-col items-center"
      // Only pass ref if this is the first tweet AND it's not yet visible
      ref={isFirstTweet && !isFirstTweetVisible ? firstTweetRef : undefined}
    >
      {renderTweetContent()}
    </div>
  );
};

const BlogDetailTemplate: React.FC<BlogDetailTemplateProps> = ({
  posts,
  articleCategories,
  remainingCategories,
  articleTags = [],
  blog,
  tags,
  author,
  maxPages,
  featuredImage,
}) => {
  const { isDesktop } = useWindowSize();

  const [fetchedDesktopArticleIds, setFetchedDesktopArticleIds] =
    useState<Maybe<string[]>>(null);

  const onFetchRelatedCategories = useCallback(
    (fetchedBlogPosts: BlogPreview[]) => {
      setFetchedDesktopArticleIds(fetchedBlogPosts.map((post) => post.id));
    },
    []
  );

  const recentArticleCount = isDesktop ? 6 : 4;
  const {
    inView: recentArticlesContainerInView,
    ref: recentArticlesContainerRef,
  } = useInView({
    triggerOnce: true,
  });

  const { ref: firstTweetRef, inView: isFirstTweetVisible } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { ref: firstGalleryRef, inView: isFirstGalleryVisible } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const renderImage = useCallback(
    ({
      isFeatureImage,
      imageTag,
      imageSrc,
      key,
    }: {
      isFeatureImage: boolean;
      imageTag: ElementData;
      imageSrc: string;
      key: number;
    }) => {
      return (
        <div
          key={key}
          className={
            isFeatureImage
              ? 'flex flex-col items-center m:px-[20px] l:mb-[60px] l:px-[180px]'
              : `${getTailwindClass(imageTag.tagName)} flex flex-col gap-s`
          }
        >
          <div>
            <Image
              className="rounded-xsmall"
              key={imageSrc}
              src={imageSrc}
              alt={isFeatureImage ? 'Article main image' : 'Article image'}
              width={1024}
              height={576}
            />
          </div>
          {'figCaption' in imageTag && (
            <div className={isFeatureImage ? 'mt-s' : undefined}>
              <span className="text-body1Light text-neutral-600">
                {imageTag.figCaption}
              </span>
            </div>
          )}
        </div>
      );
    },
    []
  );

  const renderTags = useCallback(
    (tagsToShow: ElementData[]) => {
      const isShowingFeaturedTag = tagsToShow.length === 1;
      const youTubeRegex = /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/;
      const srcAttributeRegExp = /src="([^"]*)"/;
      const isImageRegExp = /class="([^"]*wp-block-image[^"]*)"/;
      let hasSeenTweet = false;
      let hasSeenGallery = false;

      return tagsToShow.map((tag, index) => {
        if (tag.tagName === 'figure') {
          if (tag.html.match(youTubeRegex)) {
            const videoId = tag.html.match(youTubeRegex)?.[1];
            return (
              <div
                key={index}
                className={`${getTailwindClass(
                  tag.tagName
                )} flex flex-col justify-center gap-s`}
              >
                <iframe
                  className="aspect-video w-full"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Embedded YouTube"
                ></iframe>
                {tag?.figCaption && (
                  <div>
                    <span className="text-body1Light text-neutral-600">
                      {tag.figCaption}
                    </span>
                  </div>
                )}
              </div>
            );
          }

          if (tag.galleryImages && tag.galleryImages.length > 1) {
            const isFirstGallery = !hasSeenGallery;
            hasSeenGallery = true;
            return (
              <div key={index} className={getTailwindClass(tag.tagName)}>
                {!isFirstGalleryVisible ? (
                  <div ref={isFirstGallery ? firstGalleryRef : undefined}>
                    <Skeleton className="h-[254px] w-full ml:h-[576px]" />
                  </div>
                ) : (
                  <LazyCarouselGallery
                    tag={tag}
                    isFirstGallery={isFirstGallery}
                    isFirstGalleryVisible={isFirstGalleryVisible}
                    firstGalleryRef={firstGalleryRef}
                  />
                )}
              </div>
            );
          }
          const imageSrcMatch = tag.html.match(srcAttributeRegExp);
          const isImage = !!tag.html.match(isImageRegExp);

          if (isImage && imageSrcMatch && imageSrcMatch.length > 1) {
            return renderImage({
              isFeatureImage: isShowingFeaturedTag,
              imageTag: tag,
              imageSrc: imageSrcMatch[1],
              key: index,
            });
          }
        }
        if ('tweetData' in tag && tag.tweetData) {
          const isFirstTweet = !hasSeenTweet;
          hasSeenTweet = true;
          return (
            <TweetComponent
              key={index}
              tweetData={tag.tweetData}
              isFirstTweet={isFirstTweet}
              isFirstTweetVisible={isFirstTweetVisible}
              firstTweetRef={firstTweetRef}
            />
          );
        }
        return (
          <div
            key={index}
            className={getTailwindClass(tag.tagName)}
            dangerouslySetInnerHTML={{ __html: tag.html }}
          />
        );
      });
    },
    [
      isFirstTweetVisible,
      firstTweetRef,
      renderImage,
      firstGalleryRef,
      isFirstGalleryVisible,
    ]
  );

  const { data } = useInfiniteArticles({
    queryKey: ['relatedBlogPosts'],
    enabled: false,
    initialPageParam: 1,
    perPage: 4,
    exclude: [blog.id],
    categories: blog.categories.map(String).join(','),
    maxPages,
    initialData: {
      pages: [{ blogPosts: posts, nextPage: maxPages > 2 ? 2 : undefined }],
      pageParams: [1],
    },
  });

  const desktopArticleIdsToExclude: string[] = useMemo(() => {
    const relatedArticleIds = data
      ? data?.pages.flatMap((page) => page.blogPosts.map((post) => post.id))
      : null;
    const fetchedRelatedCategoryArticleIds = fetchedDesktopArticleIds || [];
    return relatedArticleIds
      ? [blog.id, ...fetchedRelatedCategoryArticleIds, ...relatedArticleIds]
      : [blog.id];
  }, [data, blog.id, fetchedDesktopArticleIds]);

  const {
    data: recentArticlesData,
    isFetching: isRecentArticlesLoading,
    isError,
    isFetched,
  } = useArticles({
    queryKey: ['recentArticles', desktopArticleIdsToExclude],
    limit: recentArticleCount,
    exclude: desktopArticleIdsToExclude,
    enabled: recentArticlesContainerInView,
  });

  const [excerpt, , , readingTimeEstimate, , , ...remainingTags] = tags;
  const [commentData, setCommentData] = useState({
    _id: '',
    upvotes: 0,
    downvotes: 0,
    numberOfComments: 0,
  });

  const authorContent = (
    <div className="flex flex-row items-center gap-s">
      {author?.avatarUrl && (
        <Avatar size={32} imageIconSrc={author.avatarUrl} />
      )}
      {author?.name && (
        <p className="text-body1Light text-white">{author.name}</p>
      )}
    </div>
  );

  useEffect(() => {
    const getNewsPost = async () => {
      // First try to get the existing news post
      const response = await fetch(`/api/community/news_posts/${blog.slug}`);

      if (response.ok) {
        const myData = await response.json();
        // Use existing post data
        setCommentData({
          _id: myData._id,
          upvotes: myData.upvoteCount || 0,
          downvotes: myData.downvoteCount || 0,
          numberOfComments: myData.commentCount || 0,
        });
      } else if (response.status === 404) {
        // Post not found, create a new one
        const createResponse = await fetch(`/api/community/news_posts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: blog.title.rendered,
            articleSlug: blog.slug, // Changed from 'slug' to 'articleSlug' to match backend
          }),
        });

        if (createResponse.ok) {
          const newData = await createResponse.json();
          setCommentData({
            _id: newData._id,
            upvotes: newData.upvotes || 0,
            downvotes: newData.downvotes || 0,
            numberOfComments: newData.commentCount || 0,
          });
        }
      }
    };

    if (blog.slug) {
      getNewsPost();
    }
  }, [blog.slug, blog.title.rendered]);

  return (
    <>
      <div className="flex flex-col">
        <div className="bg-white px-l py-s md:px-[40px] ml:px-[60px]">
          <Breadcrumb
            links={[
              {
                text: 'Home',
                href: '/',
              },
              {
                text: 'News',
                href: '/news',
                icon: <NewspaperIcon className="size-l text-blue-medium" />,
              },
              {
                href: `/news/${blog.slug}`,
                text: truncateBreadcrumbTitle(blog.title.rendered),
              },
            ]}
            renderSchema={false}
          />
        </div>
        <section className="relative flex flex-col">
          <div className="bg-dark-blue-gradient px-l py-xl ml:px-[60px] ml:pt-[64px]">
            <ArticleMetaTags
              variant="hero"
              categories={articleCategories}
              tags={articleTags}
              className="mb-s"
            />

            <h1 className="mb-l text-h3SemiBold text-white lg:mb-2xl lg:text-homepageH1SemiBold">
              {blog.title.rendered}
            </h1>
            <div
              className="mb-[40px] text-h4Regular text-white lg:mb-[30px]"
              dangerouslySetInnerHTML={{ __html: excerpt.html }}
            />

            <div className="mb-xl flex w-full flex-col gap-s md:flex-row md:justify-between md:gap-xl md:justify-self-start lg:items-center">
              <div className="flex flex-col items-start gap-xs text-center md:flex-row md:items-center md:gap-[0px]">
                {!!author && (
                  <Link href={`/news/author/${author.slug}`}>
                    {authorContent}
                  </Link>
                )}
                <div className="hidden size-2xl flex-col items-center justify-center md:flex">
                  <div className="h-l w-[1px] bg-white"></div>
                </div>
                {!!blog.date && (
                  <div className="flex flex-row gap-l">
                    <p className="text-left text-body1Light text-white ml:text-nowrap">
                      {formatUtcDateToLocalTimezone(blog.date, 'MMMM d, yyyy')}{' '}
                      {blog.modified
                        ? `| Updated ${formatUtcDateToLocalTimezone(blog.modified, 'hh:mm, MMMM d, yyyy')}`
                        : ''}
                    </p>
                  </div>
                )}
                <div className="hidden size-2xl flex-col items-center justify-center md:flex">
                  <div className="h-l w-[1px] bg-white"></div>
                </div>
                <div className="flex flex-row items-center gap-xs">
                  <Clock className="size-[12px] text-white" />
                  <div
                    className="text-body1Light text-white"
                    dangerouslySetInnerHTML={{
                      __html: readingTimeEstimate.html,
                    }}
                  ></div>
                </div>
              </div>
              <div className="hidden md:block">
                <ShareBlog containerStyle="flex flex-row gap-l" />
              </div>
            </div>
            <div
              className={clsx('block md:hidden', featuredImage ? 'mb-xl' : '')}
            >
              <ShareBlog containerStyle="flex flex-row gap-l" />
            </div>
            {!!featuredImage && (
              <div className="block l:hidden">
                <Image
                  src={featuredImage.source_url}
                  alt="cover image"
                  priority
                  width={featuredImage.width}
                  height={featuredImage.height}
                />
              </div>
            )}
          </div>
          {!!featuredImage && (
            <div className="relative hidden h-[498px] flex-col l:flex">
              <div className="flex-1 bg-dark-blue-gradient" />
              <div className="flex-1 bg-neutral-100" />
              <div className="absolute inset-x-[60px] top-0 h-full">
                <Image
                  src={featuredImage.source_url}
                  alt="cover image"
                  width={featuredImage.width}
                  height={featuredImage.height}
                  className="max-h-[498px] w-full"
                  style={{ objectFit: 'contain' }}
                  priority
                />
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="bg-neutral-100 md:pt-[40px]">
        <div className="px-l py-xl ml:px-[60px]">
          <section className="flex flex-col l:flex-row l:gap-[140px]">
            <div className="flex flex-1 flex-col gap-3xl l:basis-3/4">
              <div>{renderTags(remainingTags)}</div>

              <ArticleMetaTags
                categories={articleCategories}
                tags={articleTags}
              />

              <ArticleComments
                postId={commentData._id}
                upvotes={commentData.upvotes}
                downvotes={commentData.downvotes}
                numberOfComments={commentData.numberOfComments}
              />
            </div>
            <div className="hidden flex-1 flex-col l:flex l:basis-1/4">
              <RelatedArticlesAndCategories
                articleIdsToExclude={desktopArticleIdsToExclude}
                categories={[...articleCategories, ...remainingCategories]}
                onFetchedArticles={onFetchRelatedCategories}
              />
            </div>
          </section>

          <div className="mt-3xl">
            <RelatedNews posts={posts} />
          </div>

          <div className="my-l">
            <SubscribeBlog />
          </div>
          <div className="mb-l">
            <CarAIBanner />
          </div>

          <div className="flex w-full l:hidden">
            <RelatedArticlesAndCategories
              articleIdsToExclude={desktopArticleIdsToExclude}
              categories={[...articleCategories, ...remainingCategories]}
              onFetchedArticles={onFetchRelatedCategories}
            />
          </div>
        </div>
        <div
          className="flex w-full flex-col gap-l bg-white px-l py-xl l:gap-xl l:px-[60px]"
          ref={isFetched ? undefined : recentArticlesContainerRef}
        >
          {isError ? (
            <p>
              There was a problem fetching more articles. Please refresh the
              page and try again.
            </p>
          ) : (
            <>
              {isRecentArticlesLoading ? (
                <Skeleton className="h-2xl w-[52px]" />
              ) : (
                <h3 className="text-h3SemiBold">More</h3>
              )}
              <div className="-mx-l flex snap-x snap-mandatory flex-row gap-m overflow-x-auto scroll-px-l px-l pb-s l:mx-0 l:snap-none l:gap-xl l:px-0">
                {isRecentArticlesLoading &&
                  Array.from({ length: recentArticleCount }).map((_, index) => (
                    <div
                      className="w-[78%] max-w-[312px] shrink-0 snap-start l:w-auto l:shrink"
                      key={index}
                    >
                      <ArticleCardSkeleton />
                    </div>
                  ))}
                {recentArticlesData?.map((article) => (
                  <div
                    className="flex w-[78%] max-w-[312px] shrink-0 snap-start l:w-auto l:shrink"
                    key={article.id}
                  >
                    <ArticleCard article={article} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogDetailTemplate;
